import { useState, useEffect } from "react";
import {
  ActionButton,
  Avatar,
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Heading,
  Text,
  View,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import Emitter from "../../../emitter";
import {
  API_CALL_ANSWER,
  API_CALL_REJECT,
  CALL_STATES,
} from "../../../constants/constants";
import { RootState } from "../../../store/storeConfig";
import "../style.css";
import { CallRinging, CallReject, CallAccept } from "../../common/Icons";
import { formatLabel } from "../../../utils/utils";
import { useCustomerAttributes } from "../../../hooks/useCustomerAttributes";

const CallAcceptDialog = () => {
  const [isOpen, setOpen] = useState(true);
  const call = useSelector((state: RootState) => state.call);
  const agent = useSelector((state: RootState) => state.agent);

  const callCustomerAttributes = call?.customer?.attributes || {};
  const agentBusinessAttributes = agent?.businessAttributes || [];

  useEffect(() => {
    if (call && call?.id && call?.self?.direction !== "OUTBOUND") {
      setOpen(true);
    }
  }, [call?.id]);

  const rejectCallHandler = () => {
    Emitter.emit(API_CALL_REJECT, {
      body: { conversationId: call.self?.id },
    });
    setOpen(false);
  };

  const pickupCallHandler = () => {
    Emitter.emit(API_CALL_ANSWER, {
      body: { conversationId: call.self?.id },
    });
    setOpen(false);
  };

  const { groupedAttributes } = useCustomerAttributes({
    customerAttrs: callCustomerAttributes,
    businessAttrs: agentBusinessAttributes,
    type: "call",
  });

  const finalAttributes = (
    <View UNSAFE_className="call-incoming-attributes">
      <Flex direction="row" wrap="wrap" gap="size-200">
        {groupedAttributes.flat().map((attribute, index) => (
          <Flex
            key={`attribute-${index}`}
            direction="column"
            UNSAFE_style={{
              flex: "1 1 45%",
              minWidth: "45%",
              maxWidth: "45%",
              marginBottom: "15px",
            }}
          >
            <Text
              UNSAFE_className="call-content-heading"
              UNSAFE_style={{
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              {attribute.attributeName.toLowerCase() === "notes"
                ? "Transfer Notes"
                : formatLabel(attribute.attributeName)}
            </Text>
            <Text
              UNSAFE_className="call-content-descritpion"
              UNSAFE_style={{
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              {callCustomerAttributes[attribute.attributeName]}
            </Text>
          </Flex>
        ))}
      </Flex>
    </View>
  );

  return (
    <>
      {isOpen &&
        call?.self?.state === CALL_STATES.RINGING &&
        call?.self?.direction !== "OUTBOUND" &&
        !call?.isSilentMonitor && (
          <DialogTrigger type="modal" mobileType="modal" defaultOpen={true}>
            <ActionButton isHidden isQuiet></ActionButton>
            {() => (
              <Dialog size={"S"} UNSAFE_className="call-incoming-dialog">
                <Heading level={4}>
                  <Flex
                    direction={"row"}
                    gap={"size-100"}
                    alignItems={"center"}
                  >
                    <Text UNSAFE_className="call-heading-icon">
                      <CallRinging />
                    </Text>
                    <Text UNSAFE_className="call-heading-text">
                      New incoming call
                    </Text>
                  </Flex>
                </Heading>
                <Divider
                  marginBottom={"size-0"}
                  UNSAFE_className="call-incoming-divider"
                />
                <Content maxWidth={"100vw"} marginTop={"15px"}>
                  <Content>
                    <Flex direction={"column"} gap={"size-200"} width={"100%"}>
                      <Flex direction={"row"} width={"100%"}>
                        <Flex direction={"column"} width={"45%"}>
                          <Flex>
                            <Avatar
                              src="https://i.imgur.com/kJOwAdv.png"
                              alt="default Adobe avatar"
                              UNSAFE_className="call-avatar"
                            />
                            <Flex direction={"column"}>
                              <Text UNSAFE_className="call-content-phone-heading">
                                {call?.customer?.attributes?.Customer_ANI ||
                                  call?.customer?.displayName ||
                                  call?.customer?.phoneNumber ||
                                  "-"}
                              </Text>
                              <Text UNSAFE_className="call-content-email-descritpion">
                                -
                              </Text>
                            </Flex>
                          </Flex>
                        </Flex>
                      </Flex>
                      {finalAttributes}
                    </Flex>
                  </Content>
                </Content>
                <ButtonGroup>
                  <Button
                    UNSAFE_className="call-reject-btn"
                    variant="negative"
                    onPress={rejectCallHandler}
                    autoFocus={false}
                  >
                    <span className="reject-btn-icon">
                      <CallReject />
                    </span>
                    <Text UNSAFE_style={{ display: "contents" }}>Reject</Text>
                  </Button>
                  <Button
                    variant="accent"
                    UNSAFE_className="call-accept-btn"
                    onPress={pickupCallHandler}
                    autoFocus={false}
                  >
                    <span className="calling-btn-icon">
                      <CallAccept />
                    </span>
                    <Text UNSAFE_style={{ display: "contents" }}>Accept</Text>
                  </Button>
                </ButtonGroup>
              </Dialog>
            )}
          </DialogTrigger>
        )}
    </>
  );
};

export default CallAcceptDialog;
